import { FC } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { PickupDeliveryLocationResponseV1 } from '@forto-dev/customer-master-data-models'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'

import { LocationForm } from './LocationForm'
import { usePickupDeliveryLocationUpdateMutation } from '../../api/pickupDeliveryLocation.service'
import { LocationFormData, locationFormDataSchema } from '../../schema'
import {
  mapLocationFormToPickupDeliveryLocationUpdateRequest,
  mapPickupDeliveryLocationResponseToAddressFormData,
} from '../../utils/locationFormMapper'

type LocationEditDialogProps = {
  accountID: string
  onClose: (updatedLocation?: PickupDeliveryLocationResponseV1) => void
  pickupDeliveryLocation: PickupDeliveryLocationResponseV1
  title?: string
  description?: string
}

export const LocationEditDialog: FC<LocationEditDialogProps> = (props) => {
  const toaster = useToaster()
  const { translate } = useLocalisation()

  const {
    accountID,
    onClose,
    pickupDeliveryLocation,
    title = translate(`locations.edit.title`),
    description = translate(`locations.add.description`),
  } = props

  const selectedValue: LocationFormData = {
    address: mapPickupDeliveryLocationResponseToAddressFormData(pickupDeliveryLocation),
    companyName: pickupDeliveryLocation.address?.companyName || '',
    locationName: pickupDeliveryLocation.locationName,
    notes: pickupDeliveryLocation.notes || undefined,
    contact: pickupDeliveryLocation.contactIDs || [],
    unLocode: pickupDeliveryLocation.unLocode ?? '',
  }

  const formMethods = useForm<LocationFormData>({
    defaultValues: selectedValue,
    resolver: zodResolver(locationFormDataSchema),
  })

  const { mutate: update, isPending: isSubmitting } = usePickupDeliveryLocationUpdateMutation()

  const onSave = (formData: LocationFormData) => {
    const request = mapLocationFormToPickupDeliveryLocationUpdateRequest(accountID, formData, pickupDeliveryLocation)

    update(
      { request, id: pickupDeliveryLocation.id },
      {
        onSuccess: (updatedLocation) => {
          toaster({ variant: 'positive', title: translate('locations.edit.success') })
          onClose(updatedLocation)
        },
        onError: (error) => {
          toaster({
            variant: 'negative',
            title: translate('locations.edit.error'),
            description: error.message,
          })
        },
      }
    )
  }

  return (
    <Dialog
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <FormProvider {...formMethods}>
            <fieldset disabled={isSubmitting}>
              <LocationForm />
            </fieldset>
          </FormProvider>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline-primary">{translate('actions.cancel')}</Button>
          </DialogClose>
          <Button variant="primary" onClick={formMethods.handleSubmit(onSave)} disabled={isSubmitting}>
            {isSubmitting ? translate('locations.saving') : translate('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
